import React from 'react';
import { includes, filter, isEmpty, get, map, every, cloneDeep, find, set } from 'lodash';

// React view component to render
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { callTrendsDurationReports as CallTrendsDurationReportsView } from 'ui/views/widgets/callTrendsDurationReports';
import { CallTrendsDurationReportsService } from 'services/callTrendsDurationReports';
import { SELECT_ALL_OPTION_VALUE } from 'constants/common';

// import * as pageLevelPreloader from 'modules/page-level-preloader';
import { reportCallCallerTypeselectionResolver } from 'resolvers/reportCallCallerTypeselection';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallTrendsDurationReports } from 'ui/containers/CallTrendsDurationReports';
 *
 * // render
 * <CallTrendsDurationReports propName='propValue'/>
 */
export class CallTrendsDurationReports extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            isChartFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            barGraphData: [],
            pieChartData: [],
            graphNetworkStatus: undefined
        };

        // create service instance
        this.callTrendsDurationReportsService = new CallTrendsDurationReportsService();
        
        // start component data fetch
        this.fetchComponentData();

        // bind events and methods
        this.__bind();
    }

     // bind methods and add event listeners
     __bind() {
        this.handleOptionSelect = this.handleOptionSelect.bind( this );
        this.handleBarChartClick = this.handleBarChartClick.bind( this );
        this.handleCagChange = this.handleCagChange.bind( this );
        this.handleClearFilter = this.handleClearFilter.bind( this );
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'CallTrendsDurationReports.fetchComponentData()' );

        // cancel ongoing request
        this.callTrendsDurationReportsService.cancel();

        // get status and service data
        const { status, data } = await this.callTrendsDurationReportsService.fetchComponentData( {
            url: this.props.widget.url
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                isChartFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            const responseData = cloneDeep( data );
            const selectAll = [ { name: SELECT_ALL_OPTION_VALUE, title: { refId: '', value: 'All' }, value: SELECT_ALL_OPTION_VALUE } ];

            const callerMood = [ ...selectAll, ...get( responseData, 'options.callerMood' ) ];
            set( responseData, 'filterData.options.callerMood', callerMood );

            // fetching resolution type options
            const resolutionTypeOptions = [ ...selectAll, ...get( responseData, 'options.callResolution' ) ]; // data.options.callResolution;
            set( responseData, 'filterData.options.resolutionTypeOptions', resolutionTypeOptions );

            // fetch Caller Types from the store
            const _callerTypeOptions = cloneDeep( get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options.callerType', null ) );
            const callerTypeOptions = [ ...selectAll, ..._callerTypeOptions ];
            set( responseData, 'filterData.options.callerTypeOptions', callerTypeOptions );

            // fetch Call Types from the store
            const callCallerType = get( data, 'options.callCallerType' );
            const callCallerTypeOptions = [ ...selectAll, ...callCallerType ];

            set( responseData, 'filterData.options.callCallerType', callCallerTypeOptions );
            set( responseData, 'filterData.options.frequencyOptions', get( data, 'options.frequencyOptions' ) );


            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: responseData,
            }, () => this.fetchDataForGraph() );
        }
    }

    // fetch Data for the graphs
    async fetchDataForGraph() {
        log.debug( 'CallTrendsDurationReports.fetchDataForGraph()' );

        // cancel ongoing request
        this.callTrendsDurationReportsService.cancel();

        // remove all other Options if All is selected
        const filters = cloneDeep( get( this.state, 'componentData.filterData.filter' ) );
        Object.keys( filters ).forEach( function( key ) {
            includes( filters[ key ], SELECT_ALL_OPTION_VALUE ) ? filters[ key ] = [ SELECT_ALL_OPTION_VALUE ] : null;
        } );

        // get status and service data
        const { status, data } = await this.callTrendsDurationReportsService.fetchGraphData( {
            url: get( this.state.componentData, 'structure.filtersConfig.url' ),
            data: {
                context: {},
                data: {
                   type: 'charts',
                   filter: filters,
                }
             }
        } );

        // pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isChartFetching: false,
                graphNetworkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isChartFetching: false,
                graphNetworkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                barGraphData: data,
            } );
        }
    }
    

    // handle click of option selection
    handleOptionSelect( { option, options, selected, optionType, isSelectAllVisible, categoryId, isSingleSelect = false } ) {
		log.debug( 'CallTrendsDurationReports.handleOptionSelect()' );

        const data = { ...this.state.componentData };

        if( isSingleSelect || 'categoryAndReasons' === optionType  ) {
            data.filterData.filter [ optionType ] = selected;
        } else {

            const selectedOptions =  'callRange' !== optionType &&  'cag' !== optionType && 'frequency' !== optionType && ! isEmpty( optionType ) ? this.getSelection( option, options, selected, isSelectAllVisible ) : null;
            data.filterData.filter [ optionType ] = 'callRange' !== optionType && 'cag' !== optionType && 'frequency' !== optionType && ! isEmpty( optionType ) ? selectedOptions : options;
        }

        if( 'callType' === optionType || 'callerType' === optionType ) {
            const _filterData = reportCallCallerTypeselectionResolver( { filterData: data.filterData, optionType, optionValue: get( option, 'value' ) } );
            set( data, 'filterData', _filterData );
        }

        this.setState( {
            componentData: data,
            isChartFetching: isEmpty( categoryId ) ? true: false,
            networkStatus: undefined,
        }, () => {

            // pageLevelPreloader.show();
            this.fetchDataForGraph();
        } );
    }

    // handle click on the bar chart
    handleBarChartClick( { payload } ) {
        log.debug( 'CallTrendsDurationReports.handleBarChartClick()' );

        this.handleOptionSelect( { categoryId: payload.id } );
    }

    // get selected options
    getSelection( option, options, selected, isSelectAllVisible ) {
        log.debug( 'CallTrendsDurationReports.getSelection()' );

        // new selected value
        let selection = [];
        const selectableOptions = filter( options, _option => ! isEmpty( _option[ 'value' ] ) ); // `labels` excluded
        const selectableOptionsExceptSelectAll = isSelectAllVisible ? filter( selectableOptions, _option => _option[ 'value' ] !== SELECT_ALL_OPTION_VALUE ) : selectableOptions; // remove `selectAll` option

        if( includes( selected, option[ 'value' ] ) ) {
            if( isSelectAllVisible && option[ 'value' ] === SELECT_ALL_OPTION_VALUE ) {

                selection = []; // wipe all selection when `selectAll` option is unselected
            } else {

                selection = filter( selected, _value => {
                    return _value !== option[ 'value' ] && _value !== SELECT_ALL_OPTION_VALUE; // remove `selectAll` option as well
                } );
            }
        } else {
            if( isSelectAllVisible && option[ 'value' ] === SELECT_ALL_OPTION_VALUE ) {
                selection = map( selectableOptions, 'value' ); // select all options when `selectAll` option is selected
            } else {
                selection = selected.concat( option[ 'value' ] );
            }
        }

        // if all options are selected, also select `selectAll` option
        if( isSelectAllVisible && ! includes( selection, SELECT_ALL_OPTION_VALUE ) ) {
            const areAllOptionsSelected = every( selectableOptionsExceptSelectAll, ( _option ) => includes( selection, _option[ 'value' ] ) );

            if( areAllOptionsSelected ) {
                selection = map( selectableOptions, 'value' );
            }
        }
        return selection;
    }

    // handle cag changes when apply button is clicked
    handleCagChange( values = {} ) {
        log.debug( 'CallTrendsDurationReports.handleCagChange()' );

        this.handleOptionSelect( { options: values, optionType: 'cag' } );
    }

    // clear filter
    handleClearFilter( { optionType } ) {
        log.debug( 'CallTrendsDurationReports.handleClearFilter' );

        const data = cloneDeep( this.state.componentData );

        const type = find( get( this.state.componentData, 'structure.filtersConfig.fields' ), { name: optionType } )[ 'type' ];

        if( 'singleSelect' ===  type ) {
            data.filterData.filter [ optionType ] = '';
        } else {
            data.filterData.filter [ optionType ] = [];
        }

        this.setState( {
            componentData: data,
            isChartFetching: true,
        }, () => this.fetchDataForGraph() );
    }

    /*********************************************/
    
    /**
     * @desc When component unMounts, cleanup resources.
     */
    componentWillUnmount() {
        this.callTrendsDurationReportsService.cancel(); // cancel ongoing requests
    }
    
    /*********************************************/

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CallTrendsDurationReportsView
                isFetching={ this.state.isFetching }
                isChartFetching={ this.state.isChartFetching }
                networkStatus={ this.state.networkStatus }
                onOptionSelect={ this.handleOptionSelect }
                barGraphData={ this.state.barGraphData }
                pieChartData={ this.state.pieChartData }
                onBarChartClickHandler={ this.handleBarChartClick }
                onCagChange={ this.handleCagChange }
                onClearFilter={ this.handleClearFilter }
                { ...this.state.componentData }
                graphNetworkStatus={ this.state.graphNetworkStatus }
            />
        );
    }
}

// set display name
CallTrendsDurationReports.displayName = 'CallTrendsDurationReports';

// set default props
CallTrendsDurationReports.defaultProps = {};

// prop types
CallTrendsDurationReports.propTypes = {};
